import React, { useMemo } from 'react';
import { useSelector } from "react-redux";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, } from "recharts";
import { requestHistoryComparison } from './Services/reportTimeOffRequest';

// const data = [
//   {
//     name: "January",
//     currentYear: 12000,
//     lastYear: 42000,
//     amt: 2400,
//   },
//   {
//     name: "February",
//     currentYear: 15000,
//     lastYear: 50000,
//     amt: 2210,
//   },
//   {
//     name: "March",
//     currentYear: 40000,
//     lastYear: 25000,
//     amt: 2290,
//   },
//   {
//     name: "April",
//     currentYear: 75000,
//     lastYear: 12000,
//     amt: 2000,
//   },
//   {
//     name: "May",
//     currentYear: 58000,
//     lastYear: 25000,
//     amt: 2181,
//   },
//   {
//     name: "June",
//     currentYear: 75000,
//     lastYear: 60000,
//     amt: 2500,
//   },
//   {
//     name: "July",
//     currentYear: 47000,
//     lastYear: 40000,
//     amt: 2100,
//   },
//   {
//     name: "August",
//     currentYear: 58000,
//     lastYear: 25000,
//     amt: 2100,
//   },
//   {
//     name: "September",
//     currentYear: 40000,
//     lastYear: 40000,
//     amt: 2100,
//   },
//   {
//     name: "October",
//     currentYear: 30000,
//     lastYear: 25000,
//     amt: 2100,
//   },
//   {
//     name: "November",
//     currentYear: 30000,
//     lastYear: 40000,
//     amt: 2100,
//   },
//   {
//     name: "December",
//     currentYear: 25000,
//     lastYear: 30000,
//     amt: 2100,
//   },
// ];

const RequestHistoryGraph = ({ timeoffReq, timeoffReqLoading, timeoffReqError }) => {
  const reportingTimeoffRequest = useSelector((state) => state.reportingTimeoffRequest);

  const processedData = useMemo(() => {
    return requestHistoryComparison(
      timeoffReq,
      reportingTimeoffRequest,
    );
  }, [timeoffReq, reportingTimeoffRequest]);

  const currentYear = reportingTimeoffRequest.timeoffReqYear || new Date().getFullYear();
  const previousYear = currentYear - 1;
  const isCurrentYear = currentYear === new Date().getFullYear();

  if (timeoffReqLoading) {
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    
    // Fixed Y positions for line points to avoid random values
    const currentYearPoints = [65, 55, 45, 60, 50, 58, 70, 48, 62, 68, 72, 64];
    const lastYearPoints = [40, 35, 30, 55, 60, 52, 58, 56, 32, 45, 33, 46];
    
    return (
      <div style={{ width: '100%', height: 542 }}>
        <div style={{ 
          width: '100%', 
          height: '100%', 
          padding: '20px 30px 5px 50px',
          position: 'relative'
        }}>
          {/* Y-axis skeleton labels */}
          <div style={{ 
            position: 'absolute', 
            left: '0', 
            top: '20px', 
            bottom: '5px',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            paddingRight: '10px',
            width: '50px'
          }}>
            {Array.from({ length: 5 }).map((_, index) => (
              <div
                key={index}
                style={{
                  width: '40px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>

          {/* Chart area */}
          <div style={{ 
            marginLeft: '50px',
            height: 'calc(100% - 25px)',
            marginTop: '20px',
            marginBottom: '5px',
            position: 'relative'
          }}>
            {/* Grid lines skeleton - horizontal */}
            <div style={{ 
              position: 'absolute',
              top: '0',
              left: '0',
              right: '30px',
              bottom: '0',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between'
            }}>
              {Array.from({ length: 5 }).map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '100%',
                    height: '1px',
                    background: 'repeating-linear-gradient(to right, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {/* Vertical grid lines */}
            <div style={{ 
              position: 'absolute',
              top: '0',
              left: '0',
              right: '30px',
              bottom: '0',
              display: 'flex',
              justifyContent: 'space-between',
              paddingLeft: '50px',
              paddingRight: '0'
            }}>
              {months.map((_, index) => (
                <div
                  key={index}
                  style={{
                    width: '1px',
                    height: '100%',
                    background: 'repeating-linear-gradient(to bottom, #D6D9DB 0px, #D6D9DB 3px, transparent 3px, transparent 6px)',
                    opacity: 0.3
                  }}
                />
              ))}
            </div>

            {/* Line paths skeleton */}
            <svg 
              style={{ width: '100%', height: '100%', position: 'absolute', top: 0, left: 0 }}
              viewBox="0 0 1000 500"
              preserveAspectRatio="none"
            >
              {/* Current Year line skeleton */}
              <polyline
                points={currentYearPoints.map((yPercent, index) => {
                  const x = (index / (months.length - 1)) * 1000;
                  const y = (yPercent / 100) * 500;
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#96A8BA"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.3"
                style={{ animation: 'pulse 1.5s ease-in-out infinite' }}
              />
              {/* Last Year line skeleton */}
              <polyline
                points={lastYearPoints.map((yPercent, index) => {
                  const x = (index / (months.length - 1)) * 1000;
                  const y = (yPercent / 100) * 500;
                  return `${x},${y}`;
                }).join(' ')}
                fill="none"
                stroke="#6479F8"
                strokeWidth="2"
                strokeDasharray="5,5"
                opacity="0.3"
                style={{ animation: 'pulse 1.5s ease-in-out infinite', animationDelay: '0.2s' }}
              />
            </svg>
          </div>

          {/* X-axis skeleton ticks */}
          <div style={{ 
            position: 'absolute', 
            bottom: '5px', 
            left: '50px', 
            right: '30px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            paddingLeft: '50px'
          }}>
            {months.map((_, index) => (
              <div
                key={index}
                style={{
                  width: '45px',
                  height: '12px',
                  backgroundColor: '#e5e7eb',
                  borderRadius: '4px',
                  animation: 'pulse 1.5s ease-in-out infinite',
                  flexShrink: 0
                }}
              />
            ))}
          </div>

          {/* Legend skeleton - positioned at top right */}
          <div style={{ 
            position: 'absolute',
            top: '0',
            right: '30px',
            display: 'flex',
            gap: '20px',
            alignItems: 'center'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '2px',
                backgroundColor: '#96A8BA',
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <div style={{
                width: '100px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <div style={{
                width: '20px',
                height: '2px',
                backgroundColor: '#6479F8',
                opacity: 0.3,
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
              <div style={{
                width: '100px',
                height: '12px',
                backgroundColor: '#e5e7eb',
                borderRadius: '4px',
                animation: 'pulse 1.5s ease-in-out infinite',
              }} />
            </div>
          </div>
        </div>
        <style>{`
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.5;
            }
          }
        `}</style>
      </div>
    );
  }
  if (timeoffReqError) {
    return (
      <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6a7988' }}>
          Error loading data. Please try again.
        </div>
      </div>
    );
  }

  if (!processedData || processedData.length === 0) {
    return (
      <div style={{ width: '100%', height: 542, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ padding: '20px', textAlign: 'center', color: '#6a7988' }}>
          No data available
        </div>
      </div>
    );
  }
  return (
    <div style={{ width: "100%", height: 542 }}>
      <ResponsiveContainer>
        <LineChart
          layout="horizontal"
          data={processedData}
          margin={{
            top: 20,
            right: 30,
            left: -15,
            bottom: 5,
          }}
        >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            type="category"
            padding={{ left: 50 }}
            tickMargin={20}
          />
          <YAxis type="number" />
          <Tooltip formatter={(value) => `${value} ${value <= 1 ? 'request' : 'requests'}`} />
          <Legend />
          <Line
            dataKey="currentYear"
            name={isCurrentYear ? `Current Year (${currentYear})` : `Selected Year (${currentYear})`}
            stroke="#96A8BA"
          />
          <Line dataKey="lastYear" name={isCurrentYear ? `Last Year (${previousYear})` : `Previous Year (${previousYear})`} stroke="#6479F8" />
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
};

export default RequestHistoryGraph;
